import * as ReactQuery from "@tanstack/react-query"
import { apiFetch } from "../framework/api"
import { useAuthToken } from "./useAuthToken"
import { buildPaginationParams } from "../utils/pagination"

export type SkiFeatureSummary = {
  uid: string
  name: string
  type: string
  difficulty?: string | null
  difficulty_convention?: string | null
  is_backcountry: boolean
}

export type SkiFeatureDetail = SkiFeatureSummary & {
  geometry: string
  source: unknown
  resortName?: string
}

type SkiFeatureDetailResponse = SkiFeatureDetail | { feature: SkiFeatureDetail }

type FeatureListResponse = {
  features: SkiFeatureSummary[]
  nextCursor?: string
}

type FeatureListParams = {
  resortUid?: string
  filter?: string
  limit?: number
}

export function useResortFeatures({ resortUid, filter, limit = 10 }: FeatureListParams) {
  const token = useAuthToken()
  const { useInfiniteQuery } = ReactQuery

  return useInfiniteQuery({
    queryKey: ["resort-features", resortUid, filter, limit],
    enabled: Boolean(resortUid),
    refetchOnMount: false,
    refetchOnReconnect: false,
    queryFn: async ({ pageParam }) => {
      const params = buildPaginationParams({
        cursor: pageParam,
        filter: filter || undefined,
        limit,
      })
      const data = await apiFetch<FeatureListResponse>(`/admin/resorts/${resortUid}/features?${params}`, {
        token,
        method: "GET",
      })
      return data
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  })
}

export function useResortFeature(resortUid?: string, featureUid?: string) {
  const token = useAuthToken()
  const { useQuery } = ReactQuery

  return useQuery({
    queryKey: ["resort-feature", resortUid, featureUid],
    enabled: Boolean(resortUid && featureUid),
    queryFn: async () => {
      const data = await apiFetch<SkiFeatureDetailResponse>(
        `/admin/resorts/${resortUid}/features/${featureUid}`,
        {
          token,
          method: "GET",
        }
      )
      return "feature" in data ? data.feature : data
    },
  })
}
